"use client";
import "@/app/globals.css";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { getUserDetailsApi } from "@/app/services/users.service";
import { UserDetailsModel } from "@/app/lib/models/UserDetailsModel";
import {
  HttpStatus,
  ResponseFormatModel,
} from "@/app/lib/models/ResponseFormatModel";
import { AppConstants } from "@/app/lib/util/AppConstants";
import LoadingAnimation from "@/app/components/LoadingAnimation";
import FirebaseClientService from "@/app/lib/firebase/FirebaseClient.client";
import ContactUsButton from "@/app/components/form_elements/ContactUsButton";
import Countdown from "react-countdown";
import KovaadAnalytics from "@/app/lib/analytics/KovaadAnalytics";
import {
  goToPage,
  saveSessionDataAfterLogin,
  updateSessionStorageAndAtom,
} from "@/app/lib/util/utilities.client";
import useInitializeUserFromCookie from "@/app/components/hooks/useInitializeUserFromCookie";
import { useSetAtom } from "jotai";
import { loggedInUserDetailsAtom } from "@/app/lib/util/context.client";
import Link from "next/link";
import LoginForm from "@/app/components/login/LoginFormComponent";
import Logger from "@/lib/logger";
import logger from "@/lib/logger";
import { clearTraineeMobileChatPanel } from "@/app/lib/chat/traineeMobileChatPanel";
import {
  resolvePostLoginPath,
  stashLoginNext,
} from "@/app/lib/util/login-next-redirect.util";
import { Bounce, toast } from "@/app/lib/util/toast.client";
import {
  ACCESS_SUSPENDED_CODE,
  ACCESS_SUSPENDED_MESSAGE,
  isAccessSuspendedResponse,
} from "@/app/lib/util/access-suspension.util";
import {
  consumeAccessSuspendedNotice,
  signOutSuspendedTrainee,
} from "@/app/services/access-suspension.client";
import {
  clearAccessLinkRecipient,
  getAccessLinkMismatchMessage,
  isSameEmail,
  readAccessLinkRecipient,
} from "@/app/lib/util/access-link.util";

const USER_AUTHENTICATED_TOAST_ID = "user-authenticated";

const LoginPage: React.FC = () => {
  const [credentialsFailed, setCredentialsFailed] = useState(false);
  const [popupClosedByUser, setPopupClosedByUser] = useState(false);
  const [loading, setLoading] = useState(false);
  const [linkRecipient, setLinkRecipient] = useState<string | null>(null);
  const router = useRouter();
  const searchParams = useSearchParams();
  const currentUrl = usePathname() as string;
  const loggedInUser: UserDetailsModel | undefined =
    useInitializeUserFromCookie(currentUrl);
  const setLoggedInUser = useSetAtom(loggedInUserDetailsAtom);
  const hasRedirectedRef = useRef(false);
  const notifyUserNotFound = () =>
    toast.error("User Not Found - Please register!", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });
  const notifyPopupError = () =>
    toast.warn("Popup Closed By User", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });
  const notifyLoginSuccess = () =>
    toast.success("🦄 Kovaad Login Success!", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });
  const notifyUserAuthenticated = () =>
    toast.success("🦄 Social Login /Third-Party Login Successful", {
      toastId: USER_AUTHENTICATED_TOAST_ID,
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });
  const unauthorizedDomainError = () =>
    toast.error("🦄 Unauthorized Domain for Login", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });
  const internalServerError = () =>
    toast.error("Server Error", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });

  const backendError = () =>
    toast.error("🦄 Kovaad System Error, Please try after some time", {
      position: "top-right",
      autoClose: 5000,
      hideProgressBar: false,
      closeOnClick: true,
      pauseOnHover: true,
      draggable: true,
      progress: undefined,
      theme: "light",
      transition: Bounce,
      pauseOnFocusLoss: false,
    });

  useEffect(() => {
    KovaadAnalytics.logEvent(AppConstants.ViewsName.LOGIN);
  }, []);

  useEffect(() => {
    if (consumeAccessSuspendedNotice()) {
      toast.error(ACCESS_SUSPENDED_MESSAGE, { toastId: "access-suspended" });
    }
  }, []);

  // Keep ?next= across SSO / soft navigations that may drop the query string.
  useEffect(() => {
    stashLoginNext(searchParams?.get("next"));
  }, [searchParams]);

  useEffect(() => {
    setLinkRecipient(readAccessLinkRecipient());
  }, []);

  const onLoginSuccessUI = useCallback(
    (userDetails: UserDetailsModel) => {
      // Cookie updates after interactive login re-trigger checkCookies; only redirect once
      // so a later default /dashboard navigation cannot overwrite ?next= session deep links.
      if (hasRedirectedRef.current) {
        return;
      }
      hasRedirectedRef.current = true;
      setLoading(false);
      setCredentialsFailed(false);
      const defaultPath = getRouterPath(userDetails);
      const routerPath = resolvePostLoginPath(
        defaultPath,
        searchParams?.get("next") ?? null,
        userDetails.role,
      );
      goToPage(router, routerPath);
    },
    [router, searchParams],
  );

  const checkCookies = useCallback(() => {
    setLoading(true);
    if (loggedInUser && loggedInUser.email_id && loggedInUser.role) {
      onLoginSuccessUI(loggedInUser);
    }
    setLoading(false);
  }, [loggedInUser, onLoginSuccessUI]);

  const getRouterPath = (userDetails: UserDetailsModel): string => {
    let newRoute = "/";
    switch (userDetails.role) {
      case AppConstants.Roles.TRAINEE:
        newRoute = AppConstants.RouteUrls.TRAINEE_CHAT;
        break;
      case AppConstants.Roles.GUARDIAN:
        newRoute = AppConstants.RouteUrls.DASHBOARD;
        break;
      case AppConstants.Roles.ADMIN:
        newRoute = AppConstants.RouteUrls.DASHBOARD;
        break;
      case AppConstants.Roles.CENTER_HEAD:
        newRoute = AppConstants.RouteUrls.DASHBOARD;
        break;
    }
    return newRoute;
  };

  useEffect(() => {
    checkCookies();
  }, [checkCookies]);

  const processAfterLoginCookie = async (
    response: any,
  ): Promise<UserDetailsModel | string | undefined> => {
    notifyUserAuthenticated();
    setLoading(true);
    if (response.email) {
      try {
        const responseData: ResponseFormatModel = await getUserDetailsApi(
          response.email,
        );
        if (
          responseData.status === HttpStatus.OK &&
          responseData.data.email_id &&
          responseData.data.email_id.length > 1
        ) {
          notifyLoginSuccess();
          clearTraineeMobileChatPanel();
          const userData: UserDetailsModel = responseData.data;
          updateSessionStorageAndAtom(
            userData.email_id,
            userData,
            setLoggedInUser,
          );
          // CookieSessionManager.storeDataToBrowserSession(AppConstants.ViewsName.USER_DETAILS, userData);
          // TODO update the last login time for the user

          // Save user system details including device ID
          try {
            const { collectUserSystemDetails } =
              await import("@/app/lib/util/userSystemDetails.util");
            const { saveUserSystemDetailsApi } =
              await import("@/app/services/users.service");
            const systemDetails = await collectUserSystemDetails(
              userData.email_id,
            );
            logger.info("Collected user system details:", systemDetails);
            await saveUserSystemDetailsApi(
              systemDetails,
              responseData.data?.login_session_id,
            );
          } catch (error) {
            logger.error("Error saving user system details:", error);
            // Don't block login if system details save fails
          }
          return userData;
        } else if (isAccessSuspendedResponse(responseData)) {
          await signOutSuspendedTrainee();
          setLoading(false);
          toast.dismiss(USER_AUTHENTICATED_TOAST_ID);
          toast.error(ACCESS_SUSPENDED_MESSAGE, {
            toastId: "access-suspended",
          });
          return ACCESS_SUSPENDED_CODE;
        } else if (responseData.status === HttpStatus.ServiceUnavailable) {
          internalServerError();
          Logger.error("Internal Server Error in getting user details", {
            responseData,
          });
          goToPage(router, AppConstants.RouteUrls.ERROR as string);
        } else {
          notifyUserNotFound();
          Logger.error("User not found in the system", { responseData });
          return response;
        }
      } catch (error) {
        Logger.error("Error in getting user details", { error: String(error) });
        return undefined;
      }
    } else {
      return undefined;
    }
  };

  // TODO: Removed this because i added it back in utilities.client.ts so that after the registration the user can directly login without logging out and login again so for the guardian the flow is smooth
  /*const saveSessionDataAfterLogin = async (cookieId: string, userData: UserDetailsModel, sessionId?: string) => {
            if (userData && userData.email_id && userData.role && userData.first_name && userData.last_name) {
                const aclResponse: ResponseFormatModel = await getAclRulesApi(userData.role);
                const allowedURLs = aclResponse.data;
                const oneDay = 24 * 60 * 60 * 1000;
                const payload: CookiePayload = {
                    email: userData.email_id,
                    firstName: userData.first_name,
                    lastName: userData.last_name,
                    role: userData.role,
                    allowedURLs,
                    user_img_url: userData.img_url || "",
                    account_type: userData.account_type || AppConstants.ACCOUNT_TYPES.TRIAL,
                    premium_data_filled: userData.premium_data_filled || false,
                    trial_end_date: userData.trial_end_date || undefined,
                    sessionId
                };
                const sessionData: CookieData = {
                    name: cookieId,
                    value: payload,
                    secure: process.env.NEXT_PUBLIC_ENV === 'production',
                    sameSite: "strict",
                    path: '/',
                    expires: Date.now() + oneDay,
                    httpOnly: true
                };
                CookieSessionManager.saveLoginData(sessionData);
            }
        };*/

  /*const sendLoginSuccessEmail = async (email: string) => {
            if (email) {
                await sendEmailApi(EmailTypes.LoginSuccessful, email);
            }
        };*/

  const handleLoginFailure = async (userEmailId: string) => {
    // setEmailIdToRegister(userEmailId);
    setTimeout(() => {
      goToPage(router, AppConstants.RouteUrls.REGISTER as string);
    }, 5000);
  };

  const handleSignIn = async (signInMethod: () => Promise<any>) => {
    setLoading(true);
    try {
      const fbClient = await FirebaseClientService.getFirebaseClient();
      const result = await signInMethod.call(fbClient.getFirebaseAuthService());
      if (result && result.email) {
        const lockedRecipient = readAccessLinkRecipient();
        if (lockedRecipient && !isSameEmail(result.email, lockedRecipient)) {
          try {
            await fbClient.getFirebaseAuthService().doSignOut();
          } catch (signOutError) {
            logger.warn("Sign-out after wrong access-link account failed", {
              error: String(signOutError),
            });
          }
          setLoading(false);
          toast.error(getAccessLinkMismatchMessage(lockedRecipient), {
            toastId: "access-link-mismatch",
          });
          return;
        }
        //Store the result in session storage for registration
        const response: any = await processAfterLoginCookie(result);
        if (response === ACCESS_SUSPENDED_CODE) {
          return;
        }
        if (response && response.email_id && response.role) {
          const role = response.role as string;
          await saveSessionDataAfterLogin(role, response);
          clearAccessLinkRecipient();
          onLoginSuccessUI(response as UserDetailsModel);
        } else {
          await onLoginFailureUI(response as string);
        }
      }
    } catch (err) {
      if ((err.message as string).includes("auth/popup-closed-by-user")) {
        Logger.info("Login SSO Popup closed by the user.");
        setLoading(false);
      } else if ((err.message as string).includes("unauthorized-domain")) {
        unauthorizedDomainError();
        setTimeout(() => {
          if (process.env.NEXT_PUBLIC_ENV === "development") {
            window.location.href = "https://test.kovaad.ai";
          } else if (process.env.NEXT_PUBLIC_ENV === "production") {
            window.location.href = "https://chat.kovaad.ai";
          }
        }, 5000);
      } else {
        await onLoginFailureUI("System Error, Please check again");
      }
    }
  };

  const onGoogleSignIn = async (e) => {
    e.preventDefault();
    const fbClient = await FirebaseClientService.getFirebaseClient();
    try {
      await handleSignIn(fbClient.getFirebaseAuthService().doSignInWithGoogle);
    } catch (error) {
      throw new Error(error);
    }
  };

  const onMicrosoftSignIn = async (e) => {
    e.preventDefault();
    const fbClient = await FirebaseClientService.getFirebaseClient();
    try {
      await handleSignIn(
        fbClient.getFirebaseAuthService().doSignInWithMicrosoft,
      );
    } catch (error) {
      throw new Error(error);
    }
  };

  const requestPermission = async (email_id: string) => {
    try {
      const permission = await Notification.requestPermission();
      if (permission === "granted") {
        const fbClient = await FirebaseClientService.getFirebaseClient();
        // const resp = await fbClient.getFCMTokenAndStore(email_id);
        Logger.info("Notification permission granted.");
      } else {
        Logger.error("Notification permission denied.");
      }
    } catch (error) {
      Logger.error("Error requesting notification permission:", {
        error: String(error),
      });
    }
  };

  const onLoginFailureUI = async (
    reason: string,
    emailIdToRegister?: string,
  ) => {
    setLoading(false);
    if (reason === "Popup closed by user") {
      notifyPopupError();
      setPopupClosedByUser(true);
      setTimeout(() => {
        goToPage(router, AppConstants.RouteUrls.LOGIN as string);
        setPopupClosedByUser(false);
      }, 5000);
    } else if (
      reason &&
      (reason === "access_denied" || reason === "user_cancelled")
    ) {
      backendError();
      setTimeout(() => {
        goToPage(router, AppConstants.RouteUrls.LOGIN as string);
        setPopupClosedByUser(false);
      }, 5000);
    } else {
      setCredentialsFailed(true);
      setTimeout(() => {
        goToPage(router, AppConstants.RouteUrls.REGISTER as string);
      }, 5000);

      if (emailIdToRegister) {
        await handleLoginFailure(emailIdToRegister);
      }
    }
  };

  const renderer = ({ seconds }) => <span>{seconds}</span>;

  return (
    <main className="font">
      <Link href="/">
        <div className="md:h-[185px] md:w-[170px] h-[125px] w-[100px] bg-[url('/logo-login.svg')] bg-contain bg-no-repeat mx-auto md:mt-[2%] max-[1080px]:md:mt-[1%] mt-[8%]"></div>
      </Link>
      <div
        className={`relative items-center block w-full 2xl:mt-[5%] z-10 md:mt-[10%] max-[1080px]:md:mt-[5%] mt-[12%] overflow-hidden`}
      >
        {loading ? (
          <div role="status" className="">
            <LoadingAnimation message={""} />
          </div>
        ) : (
          <div
            className={`text-lg mt-5 text-center rounded-md shadow-lg border w-full sm:w-2/3 md:w-1/2 lg:w-1/3 max-[1080px]:lg:w-[40%] xl:w-1/4 mx-auto p-5 bg-color-white ${loading ? "bg-color-primary-base z-20 blur-[1px] grayscale" : ""}`}
          >
            <div className="font-bold md:text-[28px] text-[24px] text-color-font-dark text-center">
              Welcome!
            </div>
            <div className="md:text-[18px] text-[14px] text-center text-color-gray">
              Sign in with your preferred social account
            </div>
            {linkRecipient && (
              <div
                role="status"
                className="mt-3 rounded-lg border border-color-primary px-3 py-2 small-body-font text-color-font-dark"
              >
                This link was sent to <strong>{linkRecipient}</strong>. Please
                sign in with that account.
              </div>
            )}
            <LoginForm
              isSigningIn={loading}
              onGoogleSignIn={onGoogleSignIn}
              onMicrosoftSignIn={onMicrosoftSignIn}
            />
            {credentialsFailed && (
              <div className="text-center text-color-red">
                No user found. Please register. Page will automatically redirect
                to Registration in &nbsp;
                <Countdown date={Date.now() + 5000} renderer={renderer} />{" "}
                &nbsp; seconds
              </div>
            )}
            {popupClosedByUser && (
              <div className="text-center text-color-secondary">
                Sign up or sign in via social login above &nbsp;
              </div>
            )}
          </div>
        )}
      </div>
      <ContactUsButton label={AppConstants.CONTACT_US.LABEL} />
      <div className="fixed top-[50vh] w-full h-1/2 bg-color-bg-blue -z-10"></div>
      <div className="fixed top-0 w-full h-1/2 bg-color-white -z-10"></div>
    </main>
  );
};
export default LoginPage;
